'use client'

import { useEffect, useRef, useState } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { ChevronDownIcon, MenuIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { useHeaderChrome } from '@/components/layout/site-header-shell'
import { cn } from '@/lib/utils'
import { resolveImageSrc } from '@/lib/image-src'
import { useDictionary } from '@/lib/i18n/provider'
import type { BrandSummary, MenuNode } from '@/types/website'
import { SITE_CONTAINER } from '@/components/layout/container'

const isCurrent = (pathname: string, href: string | null) =>
  Boolean(href) && (pathname === href || (href !== '/' && pathname.startsWith(`${href}/`)))

/** Брэндийн цэс нээгддэг эсэхийг хаягаар нь таньна. */
const isBrandsItem = (item: MenuNode) => item.href === '/brands'

const hasPanel = (item: MenuNode) => item.children.length > 0 || isBrandsItem(item)

/**
 * Ширээний навигаци — дэд цэстэй элемент дээр хулгана аваачихад
 * дэлгэцийн өргөнөөр задардаг самбар гарна.
 */
export const DesktopNav = ({ items, brands }: { items: MenuNode[]; brands: BrandSummary[] }) => {
  const pathname = usePathname()
  const [openLabel, setOpenLabel] = useState<string | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const open = (label: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setOpenLabel(label)
  }

  // Богино хоцролт — цэс болон самбарын хооронд хулгана шилжихэд хаагдахгүй
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setOpenLabel(null), 150)
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpenLabel(null)

    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const active = items.find(item => item.label === openLabel && hasPanel(item))

  /*
    Том самбар нээгдэхэд толгойг тунгалагаас хатуу төлөвт шилжүүлнэ — эс тэгвээс
    тунгалаг үеийн цагаан бичгийн даралт самбарт ч тархаж, цагаан дэвсгэр дээр
    бичиг алга болно.
  */
  const { setMegaOpen } = useHeaderChrome()

  useEffect(() => {
    setMegaOpen(Boolean(active))
  }, [active, setMegaOpen])

  return (
    <div className='hidden lg:block' onMouseLeave={scheduleClose}>
      <nav className='flex items-center gap-1'>
        {items.map(item => (
          <Link
            key={item.label}
            href={item.href ?? '#'}
            target={item.target ?? undefined}
            onMouseEnter={() => (hasPanel(item) ? open(item.label) : scheduleClose())}
            onFocus={() => (hasPanel(item) ? open(item.label) : scheduleClose())}
            onClick={() => setOpenLabel(null)}
            className={cn(
              'hover:text-foreground flex items-center gap-1 rounded-md px-3 py-2 text-lg font-bold uppercase transition-colors',
              isCurrent(pathname, item.href) || openLabel === item.label ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {item.label}
            {hasPanel(item) && <ChevronDownIcon className='size-3.5' />}
          </Link>
        ))}
      </nav>

      {active && (
        <div
          onMouseEnter={() => open(active.label)}
          onClick={() => setOpenLabel(null)}
          className='bg-background/95 absolute inset-x-0 top-full border-b shadow-lg backdrop-blur'
        >
          <div className={cn(SITE_CONTAINER, 'py-8')}>
            {isBrandsItem(active) ? (
              <BrandsPanel brands={brands} />
            ) : (
              <ChildrenPanel item={active} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/** Дэд цэсүүдийг карт болгон харуулна. */
const ChildrenPanel = ({ item }: { item: MenuNode }) => (
  <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
    {item.children.map(child => (
      <Link
        key={child.label}
        href={child.href ?? '#'}
        className='hover:border-foreground/20 hover:bg-accent/40 flex flex-col gap-2 rounded-xl border p-5 transition-colors'
      >
        <span className='font-medium'>{child.label}</span>
        {child.description && <span className='text-muted-foreground text-lg text-pretty'>{child.description}</span>}
      </Link>
    ))}
  </div>
)

/**
 * Брэндийн самбар — `/brands` хуудасны «Брэндүүд» жагсаалттай ижил: брэнд
 * бүр өөрийн лого, нэртэй, дарахад шууд брэндийн хуудас руу.
 *
 * ! ӨМНӨ НЬ ҮЙЛДВЭРЛЭГЧЭЭР бүлэглэдэг байв (ОРИМИ ТРЕЙД, НОНГШИМ …) —
 * ! хэрэглэгч брэнд хайж ирээд нийлүүлэгчийн нэр харж байв.
 */
const BrandsPanel = ({ brands }: { brands: BrandSummary[] }) => {
  const t = useDictionary()

  if (brands.length === 0) {
    return <p className='text-muted-foreground py-6 text-center text-lg'>{t.brands.empty}</p>
  }

  return (
    <div className='flex flex-col gap-5'>
      <div className='flex items-center justify-between'>
        <span className='text-muted-foreground text-xs font-medium tracking-widest uppercase'>
          {t.brands.otherBrands}
        </span>
        <Link href='/brands' className='hover:text-foreground text-muted-foreground text-lg'>
          {t.common.allBrands} →
        </Link>
      </div>

      <div className='grid max-h-[60vh] gap-3 overflow-y-auto sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5'>
        {brands.map(brand => {
          const logo = resolveImageSrc(brand.logo_url)

          return (
            <Link
              key={brand.slug}
              href={`/brands/${encodeURIComponent(brand.slug)}`}
              className='hover:border-foreground/20 hover:bg-accent/40 flex items-center gap-3 rounded-xl border p-4 transition-colors'
            >
              <div className='flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-white p-1'>
                {/* Өргөн лого ч тайрагдахгүй — өргөн, өндөр хоёул хязгаартай */}
                {logo ? (
                  <Image
                    src={logo}
                    alt={brand.name}
                    width={64}
                    height={64}
                    className='max-h-full max-w-full object-contain'
                  />
                ) : (
                  <span className='text-lg font-semibold text-neutral-900'>{brand.name.charAt(0)}</span>
                )}
              </div>

              <div className='flex min-w-0 flex-col'>
                <span className='truncate font-medium'>{brand.name}</span>
                {brand.tagline && <span className='text-muted-foreground truncate text-xs'>{brand.tagline}</span>}
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

/** Гар утасны навигаци. */
export const MobileNav = ({ items }: { items: MenuNode[] }) => {
  const t = useDictionary()

  const [open, setOpen] = useState(false)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        render={
          <Button variant='ghost' size='icon' className='lg:hidden' aria-label={t.common.menu}>
            <MenuIcon className='size-5' />
          </Button>
        }
      />
      <SheetContent side='left' className='w-80 overflow-y-auto'>
        <SheetHeader>
          <SheetTitle>{t.common.menu}</SheetTitle>
        </SheetHeader>

        <div className='flex flex-col gap-1 px-4 pb-8'>
          {items.map(item => (
            <div key={item.label} className='flex flex-col'>
              <Link
                href={item.href ?? '#'}
                onClick={() => setOpen(false)}
                className='hover:bg-accent rounded-md px-3 py-2.5 font-bold uppercase'
              >
                {item.label}
              </Link>

              {item.children.map(child => (
                <Link
                  key={child.label}
                  href={child.href ?? '#'}
                  onClick={() => setOpen(false)}
                  className='text-muted-foreground hover:bg-accent rounded-md py-2 ps-6 pe-3 text-lg'
                >
                  {child.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  )
}
